import React from 'react';
import { Container, Typography, Box, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

const helpArticles = [
  // Getting Started
  {
    slug: 'creating-account',
    title: 'Creating Your Account',
    category: 'Getting Started',
    excerpt: 'Step-by-step guide to signing up and creating your IndeeClips account.',
  },
  {
    slug: 'setting-up-profile',
    title: 'Setting Up Your Profile',
    category: 'Getting Started',
    excerpt: 'Customize your profile with photo, bio, and preferences.',
  },
  {
    slug: 'understanding-homepage',
    title: 'Understanding the Homepage',
    category: 'Getting Started',
    excerpt: 'Navigate the homepage, feed, and discover personalized content.',
  },
  {
    slug: 'searching-videos',
    title: 'How to Search for Videos',
    category: 'Getting Started',
    excerpt: 'Master search filters, keywords, and discovery tools.',
  },

  // For Viewers
  {
    slug: 'watching-videos',
    title: 'Watching Videos and Clips',
    category: 'For Viewers',
    excerpt: 'Player controls, quality settings, and viewing features explained.',
  },
  {
    slug: 'subscribing-channels',
    title: 'Subscribing to Channels',
    category: 'For Viewers',
    excerpt: 'Follow your favorite creators and manage subscriptions.',
  },
  {
    slug: 'watch-history',
    title: 'Managing Your Watch History',
    category: 'For Viewers',
    excerpt: 'View, clear, and control your viewing history.',
  },
  {
    slug: 'blocking-content',
    title: 'Blocking Channels or Videos',
    category: 'For Viewers',
    excerpt: 'Hide unwanted content from your feed permanently.',
  },
  {
    slug: 'reporting-content',
    title: 'Reporting Content',
    category: 'For Viewers',
    excerpt: 'Report videos, channels, or comments that violate policies.',
  },

  // For Creators
  {
    slug: 'creating-channel',
    title: 'Creating Your Channel',
    category: 'For Creators',
    excerpt: 'Set up your creator channel and customize branding.',
  },
  {
    slug: 'uploading-videos',
    title: 'Uploading Videos',
    category: 'For Creators',
    excerpt: 'Upload process, metadata, thumbnails, and publishing tips.',
  },
  {
    slug: 'uploading-clips',
    title: 'Uploading Short Clips',
    category: 'For Creators',
    excerpt: 'Create and upload vertical short-form content.',
  },
  {
    slug: 'content-guidelines',
    title: 'Video Guidelines and Policies',
    category: 'For Creators',
    excerpt: 'Understand what content is allowed and community rules.',
  },
  {
    slug: 'channel-analytics',
    title: 'Understanding Channel Analytics',
    category: 'For Creators',
    excerpt: 'Read your dashboard data to grow your channel strategically.',
  },
  {
    slug: 'managing-videos',
    title: 'Deleting or Editing Your Videos',
    category: 'For Creators',
    excerpt: 'Modify published videos, update metadata, or remove content.',
  },

  // Account Management
  {
    slug: 'changing-password',
    title: 'Changing Your Password',
    category: 'Account Management',
    excerpt: 'Update your password securely and recover forgotten credentials.',
  },
  {
    slug: 'updating-profile',
    title: 'Updating Profile Information',
    category: 'Account Management',
    excerpt: 'Edit your profile details, email, and settings.',
  },
  {
    slug: 'deleting-account',
    title: 'Deleting Your Account',
    category: 'Account Management',
    excerpt: 'Permanently delete your account and understand data removal.',
  },
];

const Help: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const categories = [
    { name: 'Getting Started', icon: <HelpOutlineIcon />, color: '#1976d2' },
    { name: 'For Viewers', icon: <PlayCircleOutlineIcon />, color: '#388e3c' },
    { name: 'For Creators', icon: <VideoLibraryIcon />, color: '#d32f2f' },
    { name: 'Account Management', icon: <AccountCircleIcon />, color: '#f57c00' },
  ];

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Box
        sx={{
          background: (theme) =>
            `linear-gradient(160deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
          borderBottom: 1,
          borderColor: 'divider',
          py: 8,
          px: 3,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 3 }}>
            Help Center
          </Typography>
          <Typography variant="h2" component="h1" sx={{ my: 2, fontWeight: 700 }}>
            {t('help.title', 'How can we help you?')}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 300, maxWidth: '560px' }}>
            Find answers, guides, and support for all IndeeClips features
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {categories.map((category) => (
          <Box key={category.name} sx={{ mb: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box sx={{ color: category.color, mr: 2, display: 'flex' }}>
                {category.icon}
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {category.name}
              </Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              {helpArticles
                .filter((article) => article.category === category.name)
                .map((article) => (
                  <Paper
                    key={article.slug}
                    elevation={0}
                    onClick={() => navigate(`/help/${article.slug}`)}
                    sx={{
                      p: 3,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: 'primary.main',
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                      {article.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {article.excerpt}
                    </Typography>
                  </Paper>
                ))}
            </Box>
          </Box>
        ))}

        <Box
          sx={{
            mt: 8,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Still Need Help?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
            Can't find the answer you're looking for? Our support team is here to assist you.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            Contact us at{' '}
            <Typography
              component="a"
              href="mailto:contact@indeeclips.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              contact@indeeclips.com
            </Typography>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Help;
